import { Box, ClipboardCheck, PenTool } from "lucide-react";
import { useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { errorMessage } from "../api/client";
import { asSentence, planSwitch, saveThenGo, WORKSPACES, workspaceAvailability, type DesignShape, type SwitchState, type Workspace } from "../editor/workspace.ts";
import { useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { Alert, Button, Modal, useToast } from "./ui";

const ICONS: Record<Workspace, ReactNode> = { design: <Box size={15} />, sketch: <PenTool size={15} />, permit: <ClipboardCheck size={15} /> };
const LABELS: Record<Workspace, MessageKey> = { design: "designs.workspace.design", sketch: "designs.workspace.sketch", permit: "designs.workspace.permit" };
const TITLES: Record<Workspace, MessageKey> = {
  design: "designs.workspace.designTitle",
  sketch: "designs.workspace.sketchTitle",
  permit: "designs.workspace.permitTitle",
};
/** The question over unsaved work, by the view asked for (editor/workspace.ts `askText` has the same in English). */
const ASK: Record<Workspace, { title: MessageKey; body: MessageKey }> = {
  design: { title: "designs.workspace.askDesignTitle", body: "designs.workspace.askDesignBody" },
  sketch: { title: "designs.workspace.askSketchTitle", body: "designs.workspace.askSketchBody" },
  permit: { title: "designs.workspace.askPermitTitle", body: "designs.workspace.askPermitBody" },
};

/**
 * Design | Sketch | Permit, the same switch in the Designer, the Sketch tools and the Permit workspace
 * (editor/workspace.ts). `variant="sketch"` draws it with the Sketch top bar's segmented look (sketch.css).
 */
export function WorkspaceSwitch({ current, onSwitch, shortcuts, design, variant = "app" }: {
  current: Workspace;
  onSwitch: (to: Workspace) => void;
  /** Each view's key, formatted ("⇧D"), for the tooltips. */
  shortcuts: Partial<Record<Workspace, string | null>>;
  design?: DesignShape;
  variant?: "app" | "sketch";
}) {
  const t = useT();
  const blocked = design ? workspaceAvailability(design) : null;
  const sketch = variant === "sketch";
  const style = sketch ? ({ "--i": WORKSPACES.indexOf(current), "--n": WORKSPACES.length } as CSSProperties) : undefined;
  return (
    <div className={sketch ? "sk-view-switch ws-switch" : "segmented pw-mode ws-switch"} role="tablist" aria-label={t("designs.workspace.switch")} style={style}>
      {WORKSPACES.map((w) => {
        const on = w === current;
        const why = blocked?.[w] ?? null;
        const key = shortcuts[w];
        return (
          <button
            key={w}
            type="button"
            role="tab"
            aria-selected={on}
            className={on ? (sketch ? "is-active" : "on") : undefined}
            disabled={!!why && !on}
            onClick={() => !on && onSwitch(w)}
            title={why ? t("designs.workspace.needsRoof") : key ? t("designs.workspace.titleKey", { title: t(TITLES[w]), key }) : t(TITLES[w])}
            aria-keyshortcuts={key ?? undefined}
            data-workspace={w}
          >
            {ICONS[w]} <span className="ws-label">{t(LABELS[w])}</span>
          </button>
        );
      })}
    </div>
  );
}

/** What a switch does from this view: go, save (or flush) first, or ask over unsaved work. Returns `go` for the
 * switch and the keys, and the dialog to render. */
export function useWorkspaceSwitch({ designId, current, state, design, save, flush }: {
  designId: string;
  current: Workspace;
  state: SwitchState;
  design?: DesignShape;
  /** Saves the unsaved work as a new version (the editors). */
  save?: () => Promise<unknown>;
  /** Writes the autosaved work now (the Permit workspace). */
  flush?: () => Promise<unknown>;
}) {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  /** The open dialog: the question over unsaved work (`ask`), or a save or flush that failed (`flush` after a failed
   * flush; `ask` with `error` after a failed save), which keeps the user here with the reason and Retry. */
  const [asking, setAsking] = useState<{ kind: "ask" | "flush"; href: string; target: Workspace; allowWithoutSaving: boolean; error?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  /** Saves (or flushes), then goes; a no-op save goes too. A failure keeps the dialog open with the reason. */
  const run = (what: () => Promise<unknown>, dialog: NonNullable<typeof asking>) => {
    setBusy(true);
    void saveThenGo(what, () => {
      setAsking(null);
      navigate(dialog.href);
    })
      .then((outcome) => {
        if (!outcome.ok) setAsking({ ...dialog, error: errorMessage(outcome.error) });
      })
      .finally(() => setBusy(false));
  };

  const go = (to: Workspace) => {
    const plan = planSwitch(designId, current, to, state, design);
    if (plan.kind === "stay") {
      const why = design ? workspaceAvailability(design)[to] : null;
      if (why && to !== current) toast(t("designs.workspace.needsRoof"), true);
      return;
    }
    if (plan.kind === "go") return navigate(plan.href);
    if (plan.kind === "flush") {
      if (!flush) return navigate(plan.href);
      return run(flush, { kind: "flush", href: plan.href, target: to, allowWithoutSaving: false });
    }
    setAsking({ ...plan, kind: "ask" });
  };

  const saveAndGo = () => {
    if (!asking) return;
    const what = asking.kind === "flush" ? flush : save;
    if (what) run(what, asking);
  };

  const text = asking
    ? asking.kind === "flush"
      ? { title: t("designs.workspace.saveFailedTitle"), body: "" }
      : { title: t(ASK[asking.target].title), body: t(ASK[asking.target].body, { from: current }) }
    : null;
  const dialog = asking && text ? (
    <Modal
      title={text.title}
      onClose={() => !busy && setAsking(null)}
      footer={
        <>
          <Button variant="ghost" onClick={() => setAsking(null)} disabled={busy}>{t("designs.workspace.stay")}</Button>
          {asking.allowWithoutSaving && <Button onClick={() => navigate(asking.href)} disabled={busy}>{t("designs.workspace.openWithoutSaving")}</Button>}
          <Button variant="primary" loading={busy} onClick={saveAndGo} disabled={asking.kind === "flush" ? !flush : !save}>
            {asking.error ? t("designs.proposal.retry") : t(asking.target === "permit" ? "designs.workspace.saveAndOpen" : "designs.workspace.saveAndSwitch")}
          </Button>
        </>
      }
    >
      {text.body && <p style={{ margin: 0 }}>{text.body}</p>}
      {asking.error && (
        <div style={{ marginTop: text.body ? 12 : 0 }}>
          <Alert tone="danger" title={t("designs.workspace.saveFailed")}>
            {asSentence(asking.error)} {t("designs.workspace.changesKept")}
          </Alert>
        </div>
      )}
    </Modal>
  ) : null;

  return { go, dialog, busy };
}
